import React from "react";

import { CardActionsWrapper } from "@/design-system/components/Card/Card";
import { toast } from "@/design-system/components/Toast/Toast";

import { Banner } from "@/design-system/components/Banner/Banner";

import { Button } from "@/design-system/components/Button/Button";
import { Dialog } from "@/design-system/components/Dialog/Dialog";
import { Tabs } from "@/design-system/components/Tabs/Tabs";
import { makeRequest } from "@/util";
import {
  GoogleAdsIcon,
  GoogleCampaignManager360Icon,
} from "@/components/icons/themes/logos";
import { useAllSaasDestinations } from "@/hooks/useAllSaasDestinations";
import useListAllDestinationsForEnv from "../v2/hooks/useListAllDestinationsForEnv";
import { JsonFileDropzone } from "./JsonFileDropzone";

const BoxModalOAuthServiceAccount = ({ onClose, existingConfig }) => {
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();

  const allSaasDestinations = useAllSaasDestinations();
  const destName =
    allSaasDestinations[existingConfig.type_slug]?.docsShortName ||
    allSaasDestinations[existingConfig.type_slug]?.name ||
    existingConfig.type_slug;

  const [credentialsData, setCredentialsData] = React.useState(null);

  const validateFields = [
    "type",
    "project_id",
    "private_key_id",
    "private_key",
    "client_email",
    "client_id",
    "auth_uri",
    "token_uri",
    "auth_provider_x509_cert_url",
    "client_x509_cert_url",
    "universe_domain",
  ];

  const onRunOauth = () => {
    makeRequest("save_server2server_oauth", {
      google_credentials: credentialsData,
      destination_id: existingConfig?.id,
    })
      .then(() => {
        refetchDestinations();
        onClose();
      })
      .catch((error) => {
        console.error(error);
        toast.error(`Something went wrong. Unable to setup ${destName}.`);
      });
  };

  const saveEnabled = credentialsData !== null;

  const docsUrl =
    allSaasDestinations[existingConfig.type_slug]?.oauthServiceAccountHelpURL ||
    "bug: missing docs url for this integration: " + existingConfig.type_slug;

  return (
    <div>
      <div className="p-4 pb-5">
        Configure Freshpaint to use a Service Account when connecting to the
        destination Servers. You will need to upload a JSON file containing the
        service account credentials. The JSON file can be downloaded from the
        Google Cloud Console by a user who is able to manage service accounts.
        <br />
        <br />
        Please see the following link for more information on how to create a
        service account and download the credentials:
        <br />
        <a
          className="text-link underline"
          href={`${docsUrl}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {docsUrl}
        </a>
        <br />
        <br />
        <JsonFileDropzone
          validateFields={validateFields}
          onFile={setCredentialsData}
        />
        <br />
        <Banner kind="note" type="info">
          <span>
            Freshpaint&apos;s use and transfer of information received from
            Google APIs will adhere to the{" "}
            <a
              className="text-link underline"
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </span>
        </Banner>
      </div>
      <CardActionsWrapper className="p-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {saveEnabled && <Button onClick={onRunOauth}>Save</Button>}
      </CardActionsWrapper>
    </div>
  );
};

const BoxModalOAuthUserAccount = ({ onClose, existingConfig }) => {
  const allSaasDestinations = useAllSaasDestinations();
  const docsUrl =
    allSaasDestinations[existingConfig.type_slug]?.oauthUserAccountHelpURL ||
    "bug: missing docs url for this integration: " + existingConfig.type_slug;
  const destName =
    allSaasDestinations[existingConfig.type_slug]?.docsShortName ||
    allSaasDestinations[existingConfig.type_slug]?.name ||
    "bug: unknown integration name: " + existingConfig.type_slug;

  const onRunOauth = () => {
    makeRequest("get_oauth_url", {
      // Note: Using type_slug only works as long as slug in destinations.tsx matches the integrationName in oauth.go. This
      // is true for google destinations, but may not be true for every destination.
      integration_name: existingConfig.type_slug,
      destination_id: existingConfig?.id,
    })
      .then((response) => {
        window.location = response.data;
      })
      .catch((error) => {
        console.error(error);
        toast.error(`Something went wrong. Unable to setup ${destName}.`);
      });
  };

  return (
    <div>
      <div className="p-4 pb-5">
        Connect Freshpaint to the Google API using your own account. Clicking
        the button below will redirect you to Google to authenticate and
        authorize Freshpaint to access your {destName} account. Freshpaint will
        only request the minimum privileges necessary to send events to Google
        when you are prompted by google to provide permissions.
        <br />
        <br />
        Please see the docs for more information on how to connect using a User
        Account:
        <br />
        <a
          className="text-link underline"
          href={docsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {docsUrl}
        </a>
        <br />
        <br />
        Freshpaints’s use and transfer to any other app of information received
        from Google APIs will adhere to&nbsp;
        <a
          className="text-link underline"
          href="https://developers.google.com/terms/api-services-user-data-policy"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </div>
      <CardActionsWrapper className="absolute right-5 bottom-5 p-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button onClick={onRunOauth}>
          {/*
          The icon being here is part of Google's brand guidelines. The only
          destinations this modal supports are CM360 and various Ads
          destinations, so for now we pick which icon to use based on whether
          this is CM360 or not.
          */}
          {destName === "Google CM360" && (
            <GoogleCampaignManager360Icon
              className="position mr-2 fill-white"
              height="2em"
              style={{ top: -2 }}
            />
          )}
          {destName !== "Google CM360" && (
            <GoogleAdsIcon
              className="position mr-2 fill-white"
              height="2em"
              style={{ top: -2 }}
            />
          )}
          Connect With {destName}
        </Button>
      </CardActionsWrapper>
    </div>
  );
};

export function GoogleOAuthConfigModal({ open, onClose, existingConfig }) {
  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
      ariaLabel="Connect Google destination"
      contentProps={{ className: "relative h-155 max-h-175 w-175 max-w-full" }}
    >
      <Tabs.Root defaultValue="user-account">
        <Tabs.List>
          <Tabs.Trigger value="user-account">User Account</Tabs.Trigger>
          <Tabs.Trigger value="service-account">Service Account</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="user-account">
          <BoxModalOAuthUserAccount
            onClose={onClose}
            existingConfig={existingConfig}
          ></BoxModalOAuthUserAccount>
        </Tabs.Content>
        <Tabs.Content value="service-account">
          <BoxModalOAuthServiceAccount
            onClose={onClose}
            existingConfig={existingConfig}
          ></BoxModalOAuthServiceAccount>
        </Tabs.Content>
      </Tabs.Root>
    </Dialog>
  );
}
